import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, useTheme, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  Tooltip, Paper, Button, Switch, FormControlLabel,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { tokens } from "../../theme";
import authService from "../../services/AuthService";
import { fetchCardWinrates } from "../../config/cardWinratesApi";
import LazyCardImage from "../market/LazyCardImage";
import {
  CARD_ELEMENTS, CARD_RARITIES, CARD_SETS, CARD_TYPES, matchesSetCategory,
} from "../../constants/filterConstants";

/**
 * Public card winrate page (Wild) - real, counted win/play rates per card, not
 * simulated. Source: /root/bot's own battle database (CardLeagueUsage), read
 * over the 'bot_ro' connection (see elmo_burns/settings.py,
 * market/views.py:get_card_winrates).
 *
 * Same look as the private bot dashboard's CardUsageTable
 * (scenes/bot/index.jsx), minus account-specific bits (no key management, no
 * "only what you lack" toggle tied to elmo.bot's own deck). Ownership shown
 * here comes from the logged-in visitor's OWN Splinterlands collection
 * instead (same public API call MyCollection.jsx already makes).
 */

// Splinterlands stores a comma list of prints ("0,1") - the last one is the
// most recent print, and the one whose art a player is most likely holding.
const numericEdition = (raw) => {
  const parts = (raw || "").split(",").map((p) => p.trim()).reverse();
  const hit = parts.find((p) => /^\d+$/.test(p));
  return hit ? parseInt(hit, 10) : null;
};

const CardArt = ({ name, edition, cardDetailId, level, width = 58 }) => {
  const card = useMemo(() => ({ card_name: name, edition, gold: false, foil: 0, level }),
    [name, edition, level]);
  const details = useMemo(() => ({ name, edition, id: cardDetailId }),
    [name, edition, cardDetailId]);
  return (
    <Box sx={{ width, height: Math.round(width * 490 / 350), flexShrink: 0 }}>
      <LazyCardImage card={card} details={details} customLevel={level}
        alt={name} showBaseImages={false} />
    </Box>
  );
};

const SET_OPTIONS = CARD_SETS.flatMap((set) =>
  set.categories.map((cat) => ({
    key: `${set.id}_${cat.id}`, setName: set.name, catName: cat.name, icon: cat.icon, category: cat,
  })),
);
const SET_GROUPS = CARD_SETS.map((set) => ({
  id: set.id,
  name: set.name,
  options: set.categories.map((cat) => ({
    key: `${set.id}_${cat.id}`, catName: cat.name, icon: cat.icon, category: cat,
  })),
}));
const asCardDetails = (r) => ({ id: r.card_detail_id, tier: r.tier, is_promo: r.is_promo, editions: r.edition_raw });
const setOf = (r) => SET_OPTIONS.find((o) => matchesSetCategory(asCardDetails(r), o.category)) || null;
const matchesSets = (r, keys) => {
  if (!keys.length) return true;
  return keys.some((key) => matchesSetCategory(asCardDetails(r), SET_OPTIONS.find((o) => o.key === key)?.category || {}));
};

const LIGEN = [
  { id: "gold", label: "Gold" },
  { id: "diamond", label: "Diamond" },
  { id: "champion", label: "Champion" },
];

const Hint = ({ text }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Tooltip title={text} arrow>
      <HelpOutlineIcon sx={{ fontSize: 16, color: colors.blueAccent[300], cursor: "help" }} />
    </Tooltip>
  );
};

const IconFilter = ({ label, options, selected, onToggle }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box display="flex" alignItems="center" gap="6px" flexWrap="wrap">
      <Typography variant="body2" color={colors.grey[400]} sx={{ minWidth: "58px" }}>{label}</Typography>
      {options.map((o) => {
        const aktiv = selected.includes(o.id);
        return (
          <Tooltip key={o.id} arrow title={o.name}>
            <Box onClick={() => onToggle(o.id)}
              sx={{
                cursor: "pointer", width: 30, height: 30, borderRadius: "6px",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: `1px solid ${aktiv ? colors.blueAccent[500] : colors.grey[800]}`,
                backgroundColor: aktiv ? colors.blueAccent[800] : "transparent",
                opacity: aktiv ? 1 : 0.5, "&:hover": { opacity: 1 },
              }}>
              <img src={aktiv && o.activeIcon ? o.activeIcon : (o.inactiveIcon || o.icon || o.activeIcon)}
                alt={o.name} width={20} height={20}
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const SetFilterZeile = ({ selected, onToggle, onSetToggle, vorhanden }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const gruppen = SET_GROUPS
    .map((g) => ({ ...g, options: g.options.filter((o) => vorhanden.has(o.key)) }))
    .filter((g) => g.options.length);

  return (
    <Box display="flex" alignItems="center" gap="16px" flexWrap="wrap">
      {gruppen.map((g) => {
        const alle = g.options.map((o) => o.key);
        const ganzAktiv = alle.every((k) => selected.includes(k));
        return (
          <Box key={g.id} display="flex" alignItems="center" gap="5px">
            <Tooltip arrow title={ganzAktiv ? `${g.name}: deselect all` : `${g.name}: select all`}>
              <Typography variant="body2" onClick={() => onSetToggle(alle, !ganzAktiv)}
                sx={{
                  cursor: "pointer", whiteSpace: "nowrap",
                  color: alle.some((k) => selected.includes(k)) ? colors.grey[100] : colors.grey[400],
                  fontWeight: ganzAktiv ? 700 : 400,
                }}>
                {g.name}
              </Typography>
            </Tooltip>
            {g.options.map((o) => {
              const aktiv = selected.includes(o.key);
              return (
                <Tooltip key={o.key} arrow title={`${g.name} · ${o.catName}`}>
                  <Box onClick={() => onToggle(o.key)}
                    sx={{
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      width: 26, height: 26, borderRadius: "6px",
                      border: `1px solid ${aktiv ? colors.blueAccent[500] : colors.grey[800]}`,
                      backgroundColor: aktiv ? colors.blueAccent[800] : "transparent",
                      opacity: aktiv ? 1 : 0.55, "&:hover": { opacity: 1 },
                    }}>
                    <img src={o.icon} alt="" width={16} height={16}
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const CardWinratesPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const user = authService.getCurrentUser();

  const [league, setLeague] = useState("champion");
  const [minPlays, setMinPlays] = useState(100);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [owned, setOwned] = useState({});

  const [onlyMissing, setOnlyMissing] = useState(false);
  const [elemente, setElemente] = useState([]);
  const [raritaeten, setRaritaeten] = useState([]);
  const [typen, setTypen] = useState([]);
  const [sets, setSets] = useState([]);
  const [seite, setSeite] = useState(0);
  const PRO_SEITE = 50;

  const umschalten = (setzen) => (id) =>
    setzen((vorher) => (vorher.includes(id) ? vorher.filter((x) => x !== id) : [...vorher, id]));

  useEffect(() => {
    if (!user?.username) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchCardWinrates({ league, minPlays, username: user.username })
      .then((data) => { if (mounted) setRows(data.cards || []); })
      .catch((e) => { if (mounted) setError(e.message || "Failed to load"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [league, minPlays, user?.username]);

  // Own collection straight from Splinterlands' public API - same call
  // MyCollection.jsx already makes, no backend detour needed.
  useEffect(() => {
    if (!user?.username) { setOwned({}); return; }
    let mounted = true;
    fetch(`https://api.splinterlands.com/cards/collection/${user.username}`)
      .then((r) => r.json())
      .then((data) => {
        if (!mounted || !Array.isArray(data.cards)) return;
        const bestes = {};
        data.cards.forEach((c) => {
          if ((bestes[c.card_detail_id] || 0) < (c.level || 1)) {
            bestes[c.card_detail_id] = c.level || 1;
          }
        });
        setOwned(bestes);
      })
      .catch(() => { /* collection is a bonus, not a must */ });
    return () => { mounted = false; };
  }, [user?.username]);

  const vorhandeneSets = useMemo(() => {
    const keys = new Set();
    rows.forEach((r) => { const treffer = setOf(r); if (treffer) keys.add(treffer.key); });
    return keys;
  }, [rows]);

  const sichtbar = useMemo(() => rows.filter((r) => {
    if (onlyMissing && !(owned[r.card_detail_id] == null || owned[r.card_detail_id] < r.level)) return false;
    if (elemente.length && !elemente.includes(r.element)) return false;
    if (raritaeten.length && !raritaeten.includes(r.rarity)) return false;
    if (typen.length && !typen.includes(r.is_summoner ? "archons" : "units")) return false;
    if (!matchesSets(r, sets)) return false;
    return true;
  }), [rows, onlyMissing, owned, elemente, raritaeten, typen, sets]);

  useEffect(() => { setSeite(0); }, [league, minPlays, onlyMissing, elemente, raritaeten, typen, sets]);

  const seiten = Math.max(1, Math.ceil(sichtbar.length / PRO_SEITE));
  const aktuelleSeite = Math.min(seite, seiten - 1);
  const ausschnitt = sichtbar.slice(aktuelleSeite * PRO_SEITE, (aktuelleSeite + 1) * PRO_SEITE);

  if (!user?.username) {
    return (
      <Box m="20px">
        <Typography variant="h4" fontWeight="700" mb="12px">Card Winrates (Wild)</Typography>
        <Paper elevation={0} sx={{ backgroundColor: colors.primary[400], borderRadius: "14px", p: "24px" }}>
          <Typography color={colors.grey[300]}>
            Please log in with Hive Keychain to see card winrates.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Paper elevation={0} sx={{ backgroundColor: colors.primary[400], borderRadius: "14px", p: "18px" }}>
        <Box display="flex" alignItems="center" justifyContent="center" position="relative" mb="18px">
          <Box sx={{ backgroundColor: colors.primary[500], px: "18px", py: "7px", borderRadius: "10px" }}>
            <Typography variant="h5" fontWeight="700" color={colors.grey[100]}>Card Winrates (Wild)</Typography>
          </Box>
          <Box position="absolute" right={0}>
            <Hint text="Real, counted win/play rates from actually played Wild battles - not simulated." />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap="8px" mb="12px" flexWrap="wrap">
          {LIGEN.map((l) => (
            <Chip key={l.id} label={l.label} size="small" onClick={() => setLeague(l.id)}
              sx={{
                cursor: "pointer",
                backgroundColor: league === l.id ? colors.blueAccent[700] : "transparent",
                border: `1px solid ${colors.grey[700]}`,
                color: league === l.id ? colors.grey[100] : colors.grey[300],
              }} />
          ))}
          <Box display="flex" alignItems="center" gap="6px" ml="8px">
            <Typography variant="body2" color={colors.grey[400]}>from</Typography>
            {[50, 100, 200].map((n) => (
              <Chip key={n} label={`${n} battles`} size="small" onClick={() => setMinPlays(n)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: minPlays === n ? colors.greenAccent[800] : "transparent",
                  border: `1px solid ${colors.grey[700]}`,
                  color: minPlays === n ? colors.grey[100] : colors.grey[300],
                }} />
            ))}
          </Box>
          <FormControlLabel
            sx={{ ml: "8px" }}
            control={<Switch size="small" checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)} />}
            label={<Typography variant="body2" color={colors.grey[300]}>
              only what you lack
            </Typography>} />
        </Box>

        <Box display="flex" flexDirection="column" gap="8px" mb="14px">
          <Box display="flex" alignItems="center" gap="20px" flexWrap="wrap">
            <IconFilter label="Type" options={CARD_TYPES} selected={typen} onToggle={umschalten(setTypen)} />
            <IconFilter label="Element" options={CARD_ELEMENTS} selected={elemente} onToggle={umschalten(setElemente)} />
            <IconFilter label="Rarity" options={CARD_RARITIES} selected={raritaeten} onToggle={umschalten(setRaritaeten)} />
          </Box>
          {vorhandeneSets.size > 1 && (
            <Box display="flex" gap="6px" mt="4px">
              <Typography variant="body2" color={colors.grey[400]} sx={{ minWidth: "58px", pt: "2px" }}>Set</Typography>
              <SetFilterZeile selected={sets} vorhanden={vorhandeneSets}
                onToggle={umschalten(setSets)}
                onSetToggle={(keys, an) => setSets((v) => (an
                  ? [...new Set([...v, ...keys])]
                  : v.filter((k) => !keys.includes(k))))} />
            </Box>
          )}
          {(elemente.length || raritaeten.length || typen.length || sets.length) ? (
            <Box mt="4px">
              <Button size="small" variant="outlined"
                onClick={() => { setElemente([]); setRaritaeten([]); setTypen([]); setSets([]); }}
                sx={{
                  color: colors.grey[100], borderColor: colors.grey[600],
                  "&:hover": { borderColor: colors.grey[300], backgroundColor: colors.primary[500] },
                }}>
                clear filters
              </Button>
            </Box>
          ) : null}
        </Box>

        {loading ? (
          <Typography color={colors.grey[300]}>Loading...</Typography>
        ) : error ? (
          <Typography color={colors.redAccent[400]}>{error}</Typography>
        ) : !sichtbar.length ? (
          <Typography color={colors.grey[300]}>No usage data for this selection.</Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Card</TableCell>
                  <TableCell>Set</TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap="4px">
                      Players
                      <Hint text="How many DISTINCT players of this league field the card. The most trustworthy column here: unlike total plays, it cannot be inflated by a single heavy user." />
                    </Box>
                  </TableCell>
                  <TableCell align="right">Plays</TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap="4px">
                      Win rate
                      <Hint text="The win rate of the TEAMS that fielded this card - not of the card itself. Averaged across many different players, the rest of the lineup evens out." />
                    </Box>
                  </TableCell>
                  <TableCell align="right">Level</TableCell>
                  <TableCell align="right">You</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ausschnitt.map((r) => {
                  const meineStufe = owned[r.card_detail_id];
                  const set = setOf(r);
                  const edition = numericEdition(r.edition_raw);
                  return (
                    <TableRow key={r.card_detail_id} sx={{ backgroundColor: colors.primary[400] }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap="10px">
                          <CardArt name={r.name} edition={edition} cardDetailId={r.card_detail_id} level={r.level} />
                          <Box>
                            <Typography fontWeight="600">{r.name}</Typography>
                            <Typography variant="body2" color={colors.grey[400]}>
                              {r.rarity}{r.is_summoner ? " · summoner" : ""}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {set ? (
                          <Box display="flex" alignItems="center" gap="6px">
                            <img src={set.icon} alt="" width={18} height={18}
                              onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            <Typography variant="body2" color={colors.grey[200]}>{set.setName}</Typography>
                          </Box>
                        ) : <Typography variant="body2" color={colors.grey[500]}>—</Typography>}
                      </TableCell>
                      <TableCell align="right"><Typography fontWeight="700">{r.players}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" color={colors.grey[300]}>{r.plays}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="700"
                          color={r.win_rate >= 0.55 ? colors.greenAccent[400]
                            : r.win_rate >= 0.47 ? colors.grey[100] : colors.redAccent[400]}>
                          {(r.win_rate * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2" color={colors.grey[300]}>L{r.level}</Typography></TableCell>
                      <TableCell align="right">
                        {meineStufe == null ? (
                          <Typography variant="body2" color={colors.grey[500]}>—</Typography>
                        ) : (
                          <Typography variant="body2"
                            color={meineStufe >= r.level ? colors.greenAccent[400] : colors.redAccent[300]}>
                            L{meineStufe}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {seiten > 1 && (
              <Box display="flex" alignItems="center" justifyContent="center" gap="10px" mt="14px">
                <Button size="small" disabled={aktuelleSeite === 0} onClick={() => setSeite((s) => s - 1)}>
                  Previous
                </Button>
                <Typography variant="body2" color={colors.grey[400]}>
                  Page {aktuelleSeite + 1} / {seiten}
                </Typography>
                <Button size="small" disabled={aktuelleSeite >= seiten - 1} onClick={() => setSeite((s) => s + 1)}>
                  Next
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CardWinratesPage;
